import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react';
import { ImageProgressbar, useTranslation, useBookingService } from '@ibe/components';
import fallback from '../../../Translations/generated/Checkout.de.json';
import CheckoutStore from '../CheckoutStore';
import { productIBEWorkflowCheckoutBase as productIBEWorkflowCheckout } from '../../../stepsBase';
import CheckoutPageUrl from '../CheckoutPageUrl';
import useUrlPersistence from '../../Util/useUrlPersistence';
import { CheckoutStep } from '../../../Models/CheckoutStep';
import { ibeFlightClassName } from './Traveler';
import { productDetailsPageUrlStorage, productIbeModeStorage } from '../../Util/utils';
import { extendedBookingStorage } from './ExtendedBooking/ContextAndProvider';
import {
  getAvailableSteps,
  getAvailableTranslatedSteps
} from '../../../Layouts/Helper/availableSteps';
import { ApiComponentType, ApiItemType } from '@ibe/api';
import { PackageTypes } from '../../Products/ProductIBE';

const ProgressbarWrapper = observer(
  ({
    store,
    disableClick,
    rerouteUrl,
    onClick,
    isWhitelabel
  }: {
    store: CheckoutStore;
    disableClick?: boolean;
    rerouteUrl?: string;
    onClick?: (step: CheckoutStep) => void;
    isWhitelabel?: boolean;
  }) => {
    const { t } = useTranslation('Checkout', fallback);
    const navigate = useNavigate();
    const bs = useBookingService();
    const sessionPersistence = useUrlPersistence();
    const isProductIBEMode = store.isProductIBEMode
      ? store.isProductIBEMode
      : !extendedBookingStorage.get() && !!productIbeModeStorage.get();

    const components = useMemo(() => {
      return bs?.packageCart?.packageModel?.packageDetails?.[0]?.components ?? [];
    }, [bs?.packageCart?.packageModel?.packageDetails]);

    const filterExtras = useMemo(() => {
      if (store.packageType ?? sessionStorage.getItem('packageType') === PackageTypes.PFLUG) {
        return !components.some(
          component =>
            (component.itemType === ApiItemType.EXTRA && component.selectableItems.length) ||
            (component.itemType === ApiItemType.FLIGHT &&
              component.componentType === ApiComponentType.OPTIONAL &&
              component.selectableItems.length)
        );
      }
      return !components.some(
        component =>
          component.itemType === ApiItemType.EXTRA ||
          (component.itemType === ApiItemType.FLIGHT &&
            component.componentType === ApiComponentType.OPTIONAL)
      );
    }, [components]);

    const translatedSteps = useMemo(() => {
      return getAvailableTranslatedSteps(store.steps, filterExtras).map(step => {
        return { ...step, description: t(step.description) };
      });
    }, [store, t]);

    const handleClickWithoutStep = (stepIndex: number, step: CheckoutStep) => {
      if (
        isProductIBEMode &&
        !!productDetailsPageUrlStorage.get() &&
        stepIndex === 0 &&
        !isWhitelabel
      ) {
        const url = productDetailsPageUrlStorage.get();
        productDetailsPageUrlStorage.clear();
        if (url) {
          location.href = url;
        }
      } else if (!!rerouteUrl && stepIndex === 0 && !isWhitelabel) {
        location.href = rerouteUrl;
      } else {
        let url = '';
        if (stepIndex === 0 && !isWhitelabel) {
          url = sessionPersistence.get()?.url;
        }
        navigate(
          (url || Object.values(CheckoutPageUrl).find(innerUrl => innerUrl.includes(step.slug))) ??
            ''
        );
      }
    };

    const handleClick = (stepIndex: number): void => {
      if (!disableClick) {
        const workflow = getAvailableSteps(productIBEWorkflowCheckout, filterExtras);
        const step = workflow[stepIndex];
        if (step) {
          if (onClick) {
            onClick(step);
          }
          handleClickWithoutStep(stepIndex, step);
        }
      }
    };
    const hasThgHeader = document.getElementById('thg-header') !== null;
    return (
      <div
        className={`mb-4 ${hasThgHeader ? 'sticky-progressbar' : ''} ${
          isProductIBEMode ? ibeFlightClassName : ''
        }`}
      >
        <ImageProgressbar steps={translatedSteps} onClick={handleClick} showNotActiveIcon />
      </div>
    );
  }
);

export default ProgressbarWrapper;
